import { useMemo } from 'react';

const sizeMap = {
  xs: 'w-5 h-5 text-2xs',
  sm: 'w-7 h-7 text-[11px]',
  md: 'w-8 h-8 text-[11px]',
  lg: 'w-10 h-10 text-xs',
  xl: 'w-14 h-14 text-sm',
  '2xl': 'w-18 h-18 text-base',
};

const statusSizeMap = {
  xs: 'w-1.5 h-1.5 border',
  sm: 'w-2 h-2 border-[1.5px]',
  md: 'w-2.5 h-2.5 border-[1.5px]',
  lg: 'w-3 h-3 border-2',
  xl: 'w-3.5 h-3.5 border-2',
  '2xl': 'w-4 h-4 border-2',
};

const statusColors = {
  online: 'bg-success-500 shadow-glow-success',
  offline: 'bg-surface-400',
  busy: 'bg-danger-500',
  away: 'bg-warning-500',
};

const hashColor = (name) => {
  const colors = [
    'bg-primary-100 text-primary-900',
    'bg-accent-100 text-accent-800',
    'bg-success-100 text-success-800',
    'bg-warning-100 text-warning-800',
    'bg-danger-100 text-danger-800',
    'bg-blue-100 text-blue-800',
    'bg-brand-gradient text-violet-800',
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const Avatar = ({
  src,
  name = '',
  size = 'md',
  status,
  squared = false,
  className = '',
  onClick,
  ...props
}) => {
  const colorClass = useMemo(() => hashColor(name), [name]);
  const initials = useMemo(() => getInitials(name), [name]);
  const shape = squared ? 'rounded-[10px]' : 'rounded-full';

  return (
    <div
      className={`
        relative inline-flex items-center justify-center flex-shrink-0
        ${sizeMap[size]} ${shape}
        ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}
        ${className}
      `}
      title={name}
      onClick={onClick}
      {...props}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          className={`w-full h-full object-cover ${shape} ring-2 ring-white`}
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling?.classList.remove('hidden'); }}
        />
      ) : null}
      <div
        className={`
          ${src ? 'hidden' : ''}
          w-full h-full ${shape} ring-2 ring-white
          flex items-center justify-center font-bold
          ${colorClass}
        `}
      >
        {initials}
      </div>

      {status && (
        <span
          className={`
            absolute -bottom-px -right-px
            ${statusSizeMap[size]}
            ${statusColors[status]}
            rounded-full border-white
          `}
        />
      )}
    </div>
  );
};

export const AvatarGroup = ({
  users = [],
  size = 'md',
  max = 4,
  className = '',
  onOverflowClick,
}) => {
  const displayed = users.slice(0, max);
  const overflow = users.length - max;

  const overlapMap = {
    xs: '-space-x-1.5',
    sm: '-space-x-2',
    md: '-space-x-2.5',
    lg: '-space-x-3',
    xl: '-space-x-3.5',
    '2xl': '-space-x-4',
  };

  return (
    <div className={`flex items-center ${overlapMap[size]} ${className}`}>
      {displayed.map((user, i) => (
        <Avatar
          key={user.id || i}
          src={user.avatar || user.src}
          name={user.name}
          size={size}
          status={user.status}
          className="ring-2 ring-white hover:z-10 transition-transform hover:scale-110"
        />
      ))}
      {overflow > 0 && (
        <div
          className={`
            relative inline-flex items-center justify-center flex-shrink-0 rounded-full
            ring-2 ring-white text-white font-bold
            ${sizeMap[size]}
            ${onOverflowClick ? 'cursor-pointer hover:brightness-110 transition-all' : ''}
          `}
          style={{ background: 'linear-gradient(135deg, #2a2a72, #1338be)' }}
          onClick={onOverflowClick}
          title={`${overflow} more`}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
};
